import * as authActions from "@rentiohq/shared-frontend/dist/redux/auth/auth.actions";
import * as authSelectorsV1 from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import * as authActionsV2 from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.actions";
import * as authSelectors from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.selectors";
import api, {
  MCP_SOURCE_HEADERS,
} from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  DisplayText,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import {
  StyledAuthBody,
  StyledAuthContent,
  StyledAuthFrame,
  StyledAuthLogo,
  StyledAuthMain,
  StyledAuthMedia,
  StyledAuthMediaWrap,
} from "@rentiohq/web-shared/dist/scenes/Auth/Auth.styled";
import { setReturnUrlAfterLogin } from "@rentiohq/web-shared/dist/scenes/Auth/Auth.utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as S from "./ConnectAuthorize.styles";

// INFO: the consent screen of the insurance backoffice MCP connector (rentio-insurance-api,
// POST /v1/mcp/admin). The same screen and flow as the Rentio backoffice's
// (projects/rentio-backoffice/src/scenes/Connect): claude.ai sends the person to the insurance
// API's /v1/mcp/oauth/authorize, which redirects here. A signed-out person goes through the
// backoffice's own sign-in first and comes back. On approval this session mints the
// authorization code (/mcp/oauth/authorize/approve), so no password passes through the
// connector flow and the API serves no page of its own.
//
// What the resulting token may do is NOT decided here: the MCP endpoint re-reads the account on
// every request.

export const ConnectAuthorize = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const hasAccessToken = useSelector(authSelectors.hasAccessToken);
  // INFO: the signed-in account comes from the V1 auth slice, as in the Rentio backoffice screen
  // (useSelf): the sign-in saga loads the user there (getUser), and nothing fills the V2 slice's
  // user. useSelf itself is not used because it also reads broker state this app does not have.
  const user = useSelector(authSelectorsV1.getUser);

  const requestId = searchParams.get("requestId") || "";
  const clientName = searchParams.get("client") || "";

  // INFO: one flag per button, not one shared "submitting": a single flag spins the Allow
  // button while Deny is the one that was clicked.
  const [isApproving, setIsApproving] = React.useState(false);
  const [isDeclining, setIsDeclining] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  const isSubmitting = isApproving || isDeclining;
  // INFO: Allow waits until the screen can say which account is approving.
  const isUserKnown = !!user;

  const client = clientName || getLocalizedText("connect.authorize.an_app");
  const userName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
    user?.email ||
    "";

  // INFO: "not you?": sign out; the not-signed-in effect below then routes through the
  // backoffice sign-in and returns here, so someone else can approve under their own account.
  const switchAccount = () => {
    dispatch(authActionsV2.signOutStart.getAction());
  };

  // INFO: a session restored without its user (it is loaded after sign-in) fetches it, so the
  // screen never waits for a user nobody asked for.
  React.useEffect(() => {
    if (hasAccessToken && !user) {
      dispatch(authActions.getUser.actions.start({}));
    }
  }, [hasAccessToken, user, dispatch]);

  // INFO: not signed in -> remember this exact route and send the user through the backoffice
  // sign-in, which returns here on success (see Auth.utils setReturnUrlAfterLogin).
  React.useEffect(() => {
    if (!hasAccessToken) {
      setReturnUrlAfterLogin(`/connect/authorize${window.location.search}`);
      navigate("/auth/signin", { replace: true });
    }
  }, [hasAccessToken, navigate]);

  const respond = async (decision: "approve" | "decline") => {
    const setPending = decision === "approve" ? setIsApproving : setIsDeclining;
    setError(undefined);
    setPending(true);
    try {
      // INFO: the insurance API is one v1 surface; BASE_URL_INSURANCE already ends in /v1.
      const result = await api.post<{ redirectTo: string }>(
        `/mcp/oauth/authorize/${decision}`,
        { data: { requestId }, headers: MCP_SOURCE_HEADERS },
      );
      const redirectTo = result?.data?.redirectTo;
      if (redirectTo) {
        window.location.href = redirectTo;
        return;
      }
      setError(getLocalizedText("connect.authorize.failed"));
      setPending(false);
    } catch (unknownError) {
      const status = (unknownError as { response?: { status?: number } })
        ?.response?.status;
      switch (status) {
        // INFO: the session expired: sign out, and the not-signed-in effect above sends the
        // person through the sign-in and back here, request intact.
        case 401:
          setPending(false);
          switchAccount();
          return;
        case 400:
          setError(getLocalizedText("connect.authorize.expired"));
          break;
        case 403:
          setError(getLocalizedText("connect.authorize.not_allowed"));
          break;
        default:
          setError(getLocalizedText("connect.authorize.failed"));
      }
      setPending(false);
    }
  };

  // INFO: while the redirect to sign-in is in flight, render nothing.
  if (!hasAccessToken) {
    return null;
  }

  return (
    <StyledAuthFrame>
      <StyledAuthContent>
        <StyledAuthMain>
          <StyledAuthLogo />
          <StyledAuthBody>
            <DisplayText>
              {getLocalizedText("connect.authorize.title")}
            </DisplayText>
            {requestId ? (
              <>
                <S.Intro>
                  <TextStyle variation="strong">{client}</TextStyle>{" "}
                  {getLocalizedText("connect.authorize.intro")}
                </S.Intro>
                <S.Body>
                  <TextStyle variation="subdued">
                    {getLocalizedText("connect.authorize.body", { client })}
                  </TextStyle>
                </S.Body>
                {userName && (
                  <S.Identity>
                    <TextStyle variation="subdued">
                      {getLocalizedText("connect.authorize.signed_in_as")}{" "}
                      <TextStyle variation="strong">{userName}</TextStyle> —{" "}
                      {getLocalizedText("connect.authorize.not_you")}
                    </TextStyle>{" "}
                    <Button appearance="link" onClick={switchAccount}>
                      {getLocalizedText("connect.authorize.switch_account")}
                    </Button>
                  </S.Identity>
                )}
                {error && (
                  <S.ErrorMessage>
                    <TextStyle variation="negative">{error}</TextStyle>
                  </S.ErrorMessage>
                )}
                <S.Actions>
                  <Button
                    appearance="outline"
                    onClick={() => respond("decline")}
                    isDisabled={isSubmitting}
                    isSubmitting={isDeclining}
                  >
                    {getLocalizedText("connect.authorize.deny")}
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={() => respond("approve")}
                    isDisabled={isSubmitting || !isUserKnown}
                    isSubmitting={isApproving}
                  >
                    {getLocalizedText("connect.authorize.allow")}
                  </Button>
                </S.Actions>
              </>
            ) : (
              <S.Body>
                <TextStyle variation="negative">
                  {getLocalizedText("connect.authorize.expired")}
                </TextStyle>
              </S.Body>
            )}
          </StyledAuthBody>
        </StyledAuthMain>
      </StyledAuthContent>
      <StyledAuthMediaWrap>
        <StyledAuthMedia />
      </StyledAuthMediaWrap>
    </StyledAuthFrame>
  );
};

// eslint-disable-next-line import/no-default-export
export default ConnectAuthorize;
